import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import {
	Edit,
	Image as ImageIcon,
	Layers,
	Link2,
	Store,
	Tag,
} from "lucide-react"
import {
	ENTITY_PAGE_CLASS,
	EntityDetailSkeleton,
	EntityDetailSplit,
	PriceAvailabilityPanel,
	SUMMARY_LINK_CLASS,
	SummaryGroup,
	SummaryLinkContent,
	SummaryPanel,
} from "@/components/catalogue-entity/entity-detail-layout"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	PageHeader,
} from "@/components/layout"
import { AspectRatio } from "@/components/ui/aspect-ratio"
import { Badge, type BadgeProps } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { createTabSearch } from "@/hooks/use-url-tab"
import { useStagingProduct } from "@/queries/products/use-staging-product"
import type { SealedProductData } from "@/types/products.types"
import {
	type ExternalIdValue,
	formatExternalIds,
	formatGameName,
} from "@/utils/game.helpers"
import { getAdminImageUrl } from "@/utils/image.helpers"

const TABS = ["details", "marketplace"] as const
type ProductTab = (typeof TABS)[number]

export const Route = createFileRoute("/products/sealed/$id/")({
	component: SealedProductDetailPage,
	validateSearch: (search: Record<string, unknown>) => ({
		...createTabSearch(TABS)(search),
		...(typeof search.gameSlug === "string"
			? { gameSlug: search.gameSlug }
			: {}),
		...(typeof search.expansionSlug === "string"
			? { expansionSlug: search.expansionSlug }
			: {}),
	}),
})

function SealedProductDetailPage() {
	const { id } = Route.useParams()
	const searchParams = Route.useSearch()
	const navigate = useNavigate()
	const { product, isLoading, error } = useStagingProduct({
		productId: id,
		gameSlug: searchParams.gameSlug,
	})

	const activeTab = searchParams.tab ?? "details"
	const setActiveTab = (tab: string) => {
		navigate({
			to: "/products/sealed/$id",
			params: { id },
			search: (prev) => ({
				...prev,
				tab: tab === "details" ? undefined : (tab as ProductTab),
			}),
			replace: true,
		})
	}

	if (isLoading) {
		return <EntityDetailSkeleton />
	}

	if (error || !product) {
		return (
			<div className="p-4 sm:p-6">
				<PageHeader
					title="Sealed product not found"
					description={
						error?.message ||
						`The sealed product with ID ${id} could not be found.`
					}
				/>
			</div>
		)
	}

	const productData = product.data as SealedProductData
	const gameSlug = product.gameSlug
	const gameName = formatGameName(gameSlug)
	const expansionSlug = productData.expansion as string | undefined
	const productCategory = (
		productData.productCategory as string | undefined
	)?.replace(/_/g, " ")
	const languages = (productData.languages as string[] | undefined) ?? []
	const tcgPlayerIds = formatExternalIds(
		productData.externalIds?.TCGplayer as ExternalIdValue,
	)
	const cardmarketIds = formatExternalIds(
		productData.externalIds?.Cardmarket as ExternalIdValue,
	)
	const none = <span className="text-meta">None</span>

	return (
		<div className={ENTITY_PAGE_CLASS}>
			<PageHeader
				title={productData.name}
				description={
					<span className="flex flex-wrap items-center gap-x-2">
						<span className="font-mono text-foreground">
							{product.entitySlug}
						</span>
						<span className="text-meta">·</span>
						<span>{gameName}</span>
						{productCategory && (
							<>
								<span className="text-meta">·</span>
								<span className="capitalize">{productCategory}</span>
							</>
						)}
						{expansionSlug && (
							<>
								<span className="text-meta">·</span>
								<span>{expansionSlug}</span>
							</>
						)}
					</span>
				}
				actions={
					<Button asChild>
						<Link
							to="/products/sealed/$id/manage"
							params={{ id }}
							search={{
								gameSlug: product?.gameSlug,
								expansionSlug,
							}}
						>
							<Edit />
							Edit
						</Link>
					</Button>
				}
			/>

			<EntityDetailSplit
				aside={
					<>
						<SummaryPanel title="Image" icon={ImageIcon}>
							<SummaryGroup>
								<AspectRatio
									ratio={5 / 7}
									className="overflow-hidden rounded-md bg-muted"
								>
									{productData.image ? (
										<img
											src={getAdminImageUrl(
												typeof productData.image === "object" &&
													productData.image !== null
													? productData.image.source ||
															productData.image.destination ||
															""
													: productData.image,
												{ width: 600 },
											)}
											alt={productData.name || "Product image"}
											className="h-full w-full object-contain"
										/>
									) : (
										<div className="flex h-full items-center justify-center text-meta">
											No image available
										</div>
									)}
								</AspectRatio>
							</SummaryGroup>
						</SummaryPanel>

						<SummaryPanel title="Related content" icon={Link2}>
							<SummaryGroup>
								<Button asChild variant="ghost" className={SUMMARY_LINK_CLASS}>
									<Link to="/games/$id" params={{ id: gameSlug }}>
										<SummaryLinkContent
											icon={<Layers />}
											label="View game details"
											value={gameName}
										/>
									</Link>
								</Button>
								{expansionSlug && (
									<Button
										asChild
										variant="ghost"
										className={SUMMARY_LINK_CLASS}
									>
										<Link to="/catalogue/games/$gameSlug" params={{ gameSlug }}>
											<SummaryLinkContent
												icon={<Layers />}
												label="View expansion"
												value={expansionSlug}
											/>
										</Link>
									</Button>
								)}
							</SummaryGroup>
						</SummaryPanel>
					</>
				}
			>
				<div className="flex flex-col gap-4">
					<Tabs
						value={activeTab}
						onValueChange={setActiveTab}
						className="gap-6"
					>
						<TabsList variant="line">
							<TabsTrigger value="details">Details</TabsTrigger>
							<TabsTrigger value="marketplace">Marketplace</TabsTrigger>
						</TabsList>

						<TabsContent value="details">
							<DetailPanel title="Product details" icon={Tag}>
								<DetailPanelSection>
									<div className="grid gap-x-8 gap-y-2 md:grid-cols-2">
										<KeyValueList>
											<KeyValueItem label="Game">
												<Badge variant={gameSlug as BadgeProps["variant"]}>
													{gameName}
												</Badge>
											</KeyValueItem>
											<KeyValueItem
												label="Product category"
												className="capitalize"
											>
												{productCategory || none}
											</KeyValueItem>
											<KeyValueItem label="Card count">
												{(productData.cardCount as number) || none}
											</KeyValueItem>
											<KeyValueItem label="Release date">
												{(productData.releaseDate as string) || none}
											</KeyValueItem>
											{productData.ean && (
												<KeyValueItem label="EAN" className="font-mono">
													{productData.ean as string}
												</KeyValueItem>
											)}
											{productData.sku && (
												<KeyValueItem label="SKU" className="font-mono">
													{productData.sku as string}
												</KeyValueItem>
											)}
											<KeyValueItem
												label="Languages"
												className="font-mono uppercase"
											>
												{languages.length > 0 ? (
													languages.join(" · ")
												) : (
													<span className="font-sans normal-case text-meta">
														None
													</span>
												)}
											</KeyValueItem>
										</KeyValueList>
										<KeyValueList>
											<KeyValueItem label="Slug" className="font-mono">
												{product.entitySlug}
											</KeyValueItem>
											{product._id && (
												<KeyValueItem label="Product ID" className="font-mono">
													{typeof product._id === "string"
														? product._id
														: String(product._id)}
												</KeyValueItem>
											)}
											<KeyValueItem label="Expansion">
												{expansionSlug || none}
											</KeyValueItem>
											<KeyValueItem label="Last updated">
												{new Date(
													product._staging.lastModified,
												).toLocaleString()}
											</KeyValueItem>
										</KeyValueList>
									</div>
								</DetailPanelSection>
							</DetailPanel>
						</TabsContent>

						<TabsContent value="marketplace">
							<DetailPanel title="Marketplace IDs" icon={Store}>
								<DetailPanelSection>
									{tcgPlayerIds.length === 0 && cardmarketIds.length === 0 ? (
										<p className="text-meta">No marketplace IDs available</p>
									) : (
										<KeyValueList>
											{tcgPlayerIds.map(({ finish, id }) => (
												<KeyValueItem
													key={`tcg-${finish}-${id}`}
													label={`TCGplayer ID${finish ? ` (${finish})` : ""}`}
													className="font-mono"
												>
													{id}
												</KeyValueItem>
											))}
											{cardmarketIds.map(({ finish, id }) => (
												<KeyValueItem
													key={`cm-${finish}-${id}`}
													label={`Cardmarket ID${finish ? ` (${finish})` : ""}`}
													className="font-mono"
												>
													{id}
												</KeyValueItem>
											))}
										</KeyValueList>
									)}
								</DetailPanelSection>
							</DetailPanel>
						</TabsContent>
					</Tabs>

					<PriceAvailabilityPanel />
				</div>
			</EntityDetailSplit>
		</div>
	)
}
